import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Leaf } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/site";

const Wordmark = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 text-forest">
    <Leaf className="h-5 w-5 text-sage" strokeWidth={1.6} />
    <span className="font-heading text-2xl font-medium uppercase tracking-[0.18em]">
      {SITE.name}
    </span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 z-50 w-full transition-colors duration-300 ${
          scrolled ? "border-b border-line/60 bg-cream/80 backdrop-blur-xl" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
          <Wordmark />

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group relative text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-forest" : "text-ink/70 hover:text-forest"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-1.5 left-0 h-px bg-sage transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/products"
            data-testid="nav-cta"
            className="hidden rounded-full bg-sage px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-forest lg:inline-block"
          >
            Shop Collection
          </Link>

          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            className="text-forest lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-7 w-7" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex flex-col bg-cream p-6 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Wordmark />
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu">
                <X className="h-7 w-7 text-forest" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06 }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="block border-b border-line/70 py-4 font-heading text-3xl text-forest"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <Link
              to="/products"
              onClick={() => setOpen(false)}
              className="mt-auto rounded-full bg-sage py-4 text-center font-semibold text-white"
            >
              Shop Collection
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
